import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Alert, Col, Row, Space, Switch } from 'antd'
import { PrimaryButton } from 'Components/button'
import {
  CampaignEventType,
  CampaignType,
  CampaignWithProduct,
  CouponMinimumType,
  CouponType,
  menuCouponType,
} from 'helpers/constanst'
import {
  isFormRequired,
  isFormError,
  formValidation,
  getCouponStatus,
  getCouponMinimumType,
  getCouponProductType,
  isAfter,
} from 'helpers/format'
import { CampaignProducts, CreateReq, DetailReq, UpdateReq } from 'Store/coupon/types'
import { createCouponAPI, getCouponDetailAPI, updateCouponAPI } from 'Store/coupon/api'
import { selectActionEvent, selectDetail, selectDetailEvent } from 'Store/coupon/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { LabelItem, AppInput, AppSelect, AppRangeDateItem, AppTextArea, FormItem } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/coupon/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import ProductsDrawer from './products'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const CouponDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = [
    'campaign_name',
    'start_date',
    'end_date',
    'expired_time',
    'discount_type',
    'discount_amount',
    //'total_coupon',
    'minimum_type',
    'description',
    'maximum_pre_user',
  ]
  const initForm = {
    campaign_name: '',
    end_date: '',
    start_date: '',
    expired_time: 0,
    discount_type: CouponType.AMOUNT,
    discount_amount: 0,
    total_coupon: 0,
    minimum_type: CouponMinimumType.UNLIMIT,
    description: '',
    minimum: 0,
    publish: false,
    campaign_type: CampaignType.COUPON,
    event_type: CampaignEventType.REGISTER,
    maximum_pre_user: 0,
    products: [],
    status_use_product: CampaignWithProduct.ALL,
    merchant_ids: [currentUser?.activeMerchant?.id || 0],
  } as CreateReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const [chooseProduct, setChooseProduct] = useState(false)
  const dispatch = useDispatch()

  const requireMinimumAmount = formData.minimum_type === CouponMinimumType.MINIMUM
  const requireProductId = formData.status_use_product === CampaignWithProduct.VERIFY
  const isAmountType = formData.discount_type === CouponType.AMOUNT
  const isPublic = formData.publish
  const title = isCreateMode ? 'สร้างแคมเปญคูปอง' : 'แก้ไขแคมเปญคูปอง'
  const isReadonly = mode === 'edit' && isAfter(formData.start_date)

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }
    // get banner detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetBannerDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...detail,
      } as CreateReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateReq = {
      ...formData,
    }
    dispatch(createCouponAPI({ data: req }, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onGetBannerDetail = (): void => {
    const req: DetailReq = {
      id,
    }
    dispatch(getCouponDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateReq = {
      ...formData,
      id,
    }
    dispatch(updateCouponAPI({ data: req }, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    if (requireMinimumAmount) {
      if (!formData.minimum || formData.minimum <= 0) {
        error.push('minimum')
      }
    }

    if (requireProductId) {
      if (formData.products.length <= 0) {
        error.push('products')
      }
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const handleChangeDate = (statDate: string, endDate: string) => {
    setFormData({
      ...formData,
      start_date: statDate,
      end_date: endDate,
    })
  }

  const handleToggleMinimumType = (val: boolean) => {
    setFormData({
      ...formData,
      minimum_type: val ? CouponMinimumType.MINIMUM : CouponMinimumType.UNLIMIT,
    })
  }

  const handleToggleProductType = (val: boolean) => {
    setFormData({
      ...formData,
      status_use_product: val ? CampaignWithProduct.VERIFY : CampaignWithProduct.ALL,
    })
  }

  const handleToggleStatus = (val: boolean) => {
    setFormData({
      ...formData,
      publish: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  const ChooseProductsDrawer = (
    <ProductsDrawer
      visible={chooseProduct}
      onCloseDrawer={() => setChooseProduct(false)}
      onSaveProduct={(products: CampaignProducts[]) => {
        onUpdateData(products, 'products')
        setChooseProduct(false)
      }}
      products={formData.products}
    />
  )

  return (
    <DetailDrawer title={title} onCloseDrawer={onCloseDrawer} visible={visible} footer={FooterContent}>
      {ChooseProductsDrawer}
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={24} xs={24}>
          <AppInput
            name="campaign_name"
            label="ชื่อโปรโมชั่น"
            isRequired={isRequired('campaign_name')}
            isError={isError('campaign_name')}
            value={formData.campaign_name}
            onChange={onUpdateData}
            resProps={{
              disabled: isReadonly,
            }}
          />
        </Col>
        <Col md={24} xs={24}>
          <AppRangeDateItem
            onChange={handleChangeDate}
            startDate={formData.start_date}
            endDate={formData.end_date}
            label={'วันที่แจกคูปอง'}
            isRequired={isRequired('start_date')}
            isError={isError('start_date')}
            placeHolder={['วันที่เริ่มเริ่มแจก', 'วันที่สิ้นสุด']}
            disabled={[isReadonly, false]}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppSelect
            name="discount_type"
            label="ประเภทส่วนลด"
            isRequired={isRequired('discount_type')}
            isError={isError('discount_type')}
            value={formData.discount_type}
            onChange={onUpdateData}
            menu={menuCouponType}
            disabled={isReadonly}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            type="number"
            name="discount_amount"
            label="มูลค่า"
            isRequired={isRequired('discount_amount')}
            isError={isError('discount_amount')}
            value={formData.discount_amount}
            onChange={(val: string, key: string) => onUpdateData(parseInt(val), key)}
            resProps={{ suffix: isAmountType ? 'บาท' : 'เปอร์เซ็นต์', disabled: isReadonly }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            type="number"
            name="expired_time"
            label="จำนวนวันที่ใช้งานได้"
            isRequired={isRequired('expired_time')}
            isError={isError('expired_time')}
            value={formData.expired_time}
            onChange={(val: string, key: string) => onUpdateData(parseInt(val), key)}
            resProps={{ suffix: 'วัน', disabled: isReadonly }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            type="number"
            name="total_coupon"
            label="จำนวนคูปอง"
            remark=" (ถ้าระบุเป็น 0 คือไม่จำกัดจำนวนคูปอง)"
            isRequired={isRequired('total_coupon')}
            isError={isError('total_coupon')}
            value={formData.total_coupon}
            onChange={(val: string, key: string) => onUpdateData(parseInt(val), key)}
            resProps={{ suffix: 'ใบ' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            type="number"
            name="maximum_pre_user"
            label="จำนวนที่แจกสูงสุดต่อผู้ใช้ 1 คน"
            isRequired={isRequired('maximum_pre_user')}
            isError={isError('maximum_pre_user')}
            value={formData.maximum_pre_user}
            onChange={(val: string, key: string) => onUpdateData(parseInt(val), key)}
            resProps={{ disabled: isReadonly, suffix: 'ใบ' }}
          />
        </Col>
        <Col md={24} xs={24}>
          <AppTextArea
            label="เงื่อนไขการใช้คูปอง"
            isRequired={isRequired('description')}
            isError={isError('description')}
            name="description"
            rows={4}
            value={formData.description}
            onChange={onUpdateData}
            resProps={{ disabled: isReadonly }}
          />
        </Col>
        <Col md={24} xs={24}>
          <SwitchStyled>
            <Switch checked={requireProductId} onChange={handleToggleProductType} disabled={isReadonly} />{' '}
            &nbsp;
            <LabelItem label={`สินค้าที่เข้าร่วมแคมเปญคูปองส่วนลด : `} />
            <PrimaryColorText>{getCouponProductType(formData.status_use_product)}</PrimaryColorText>
          </SwitchStyled>
        </Col>
        {requireProductId && (
          <Col md={24} xs={24}>
            <InputMargin>
              <FormItem
                label=""
                status={requireProductId && isError('products') ? 'error' : ''}
                error={`กรุณาระบุสินค้าที่เข้าร่วมแคมเปญคูปองส่วนลด อย่างน้อย 1 รายการ`}
              >
                จำนวนสินค้าที่เลือก {formData.products.length} รายการ&nbsp;{' '}
                <LinkProduct onClick={() => setChooseProduct(true)}>เลือกสินค้า</LinkProduct>
              </FormItem>
            </InputMargin>
          </Col>
        )}
        <Col md={24} xs={24}>
          <SwitchStyled>
            <Switch checked={requireMinimumAmount} onChange={handleToggleMinimumType} disabled={isReadonly} />{' '}
            &nbsp;
            <LabelItem label={`ยอดขั้นต่ำของตะกร้า : `} />
            <PrimaryColorText>{getCouponMinimumType(formData.minimum_type)}</PrimaryColorText>
          </SwitchStyled>
        </Col>
        {requireMinimumAmount && (
          <Col md={24} xs={24}>
            <InputMargin>
              <AppInput
                type="number"
                name="minimum"
                label="ราคาขั้นต่ำที่กำหนด"
                placeHolder="ราคาขั้นต่ำที่กำหนด"
                isRequired={requireMinimumAmount}
                isError={isError('minimum')}
                value={formData.minimum}
                onChange={(val: string, key: string) => onUpdateData(parseInt(val), key)}
                resProps={{ suffix: 'บาท', disabled: isReadonly }}
              />
            </InputMargin>
          </Col>
        )}
        <Col md={24} xs={24}>
          <SwitchStyled>
            <Switch checked={isPublic} onChange={handleToggleStatus} disabled={isReadonly} /> &nbsp;
            <LabelItem label={`สถานะ : `} />
            {isPublic ? (
              <GreenColorText>{getCouponStatus(formData.publish)}</GreenColorText>
            ) : (
              <ErrrorColorText>{getCouponStatus(formData.publish)}</ErrrorColorText>
            )}
          </SwitchStyled>
        </Col>
        {isCreateMode && (
          <Col md={24} xs={24}>
            <br />
            <Alert
              message="ข้อกำหนดการสร้างแคมเปญแจกคูปอง"
              description={
                <RemarkText>
                  แคมเปญการแจกคูปองที่ถูกสร้าง เมื่อแคมเปญนั้น ๆ ถึงวันกำหนดเปิดใช้งานตามที่ตั้งค่าไว้
                  แคมเปญดังกล่าวจะไม่สามารถแก้ไขข้อมูลใด ๆ ได้
                  ยกเว้นการแก้ไขวันสิ้นสุดแคมเปญและจำนวนคูปองที่ต้องการแจกเท่านั้น
                </RemarkText>
              }
              type="error"
            />
          </Col>
        )}
      </Row>
    </DetailDrawer>
  )
}

const SwitchStyled = styled.div`
  margin: 10px 0;
`

const InputMargin = styled.div`
  margin-left: 55px;
`

const LinkProduct = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
`

const RemarkText = styled.span`
 // color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const PrimaryColorText = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const GreenColorText = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.success};
`

const ErrrorColorText = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    detail: selectDetail(state),
    detailEvent: selectDetailEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(CouponDetail)
